import React, {
  FormEvent,
  useMemo,
  useRef,
  useState,
  useCallback,
  Fragment
} from 'react'
import { useHistory } from 'react-router'
import moment from 'moment-timezone'
import { toast } from 'react-toastify'
import { Menu } from '@material-ui/core'
import CustomStore from 'devextreme/data/custom_store'
import {
  Column,
  Paging,
  Pager,
  LoadPanel,
  RemoteOperations
} from 'devextreme-react/data-grid'
import DataGrid from 'presentation/shared/components/DataGrid'
import { TooltipNew } from 'presentation/shared/components/TooltipNew'
import { PaginationModelNew } from 'domain/entities/pagination-model'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useServices } from 'presentation/hooks/use-services'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { ReactComponent as CalendarRange } from 'presentation/assets/icons/calendar-range.svg'
import { ReactComponent as AlertIcon } from 'presentation/assets/icons/alert-icon-new.svg'
import { ReactComponent as TriangleAlertIcon } from 'presentation/assets/icons/alert-triangle-filled.svg'
import { ReactComponent as IntegratedSurgicalOrderIcon } from 'presentation/assets/icons/integrated-surgical-order.svg'
import { ReactComponent as OtherSurgeriesIcon } from 'presentation/assets/icons/other-surgeries-icon.svg'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'
import { dateMask } from 'presentation/utils/masks'
import * as S from './styles'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { dateInputFormat } from 'presentation/utils/date-format'
import { useStores } from 'presentation/hooks/use-stores'
import { PasswordMaxDateValidationStatus } from 'common/enum/password-max-date-validation-status'
import ScrollView from 'devextreme-react/scroll-view'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import { capitalizeText } from 'common/utils/capitalizeText'
import { getShortName } from 'common/utils/getShortName'
import { getNameInitials } from 'common/utils/getNameInitials'
import normalizeText from 'common/utils/getNormalizedText'
import { capitalizeEachWordText } from 'common/utils/capitalizeEachWordText'
import {
  SchedulingStatus,
  SchedulingStatusLocated
} from 'common/enum/scheduling-status'
import { SurgeryType, SurgeryTypeLocated } from 'common/enum/surgery-type'
import { dateIntervals } from 'common/utils/getDateIntervals'
import { SurgicalSchedulingSectorModel } from 'domain/entities/surgical-scheduling-sector-model'
import { SurgicalScheduleModel } from 'domain/entities/surgical-schedule-model'
import ScheduleDetailsModal from '../../ScheduleDetailsModal'

type Props = {
  status?: SchedulingStatus
  currentHospitalId: number
  setTitleStatus: (status: SchedulingStatus) => void
} & WithLoadingProps

type IntervalProps = {
  start?: string
  end?: string
  label?: string
}

const SchedulingSectorOrdersDataGrid = WithLoading(
  ({ status, currentHospitalId, setIsLoading, setTitleStatus }: Props) => {
    const history = useHistory()
    const surgicalOrderService = useServices().surgicalOrder
    const surgicalScheduleService = useServices().surgicalSchedule
    const [selectedStatus, setSelectedStatus] = useState<SchedulingStatus>(
      status ?? SchedulingStatus.ALL
    )
    const [schedule, setSchedule] = useState<SurgicalScheduleModel>(
      {} as SurgicalScheduleModel
    )
    const [showModalScheduleDetails, setShowModalScheduleDetails] =
      useState<boolean>(false)
    const [searchField, setSearchField] = useState<string>('')
    const currentFilters = useStores().currentFilters
    const crmoFilters = currentFilters.crmoFilter
    const [dates, setDates] = useState<IntervalProps>(
      crmoFilters || {
        end: dateIntervals.END_OF_TODAY.toISOString(),
        start: dateIntervals.START_OF_TODAY.toISOString()
      }
    )
    const [showCalendar, setShowCalendar] = useState<'start' | 'end' | ''>()
    const dataGridRef = useRef<any>(null)
    const allowedPageSizes = [5, 10, 15, 20, 30]
    const [paginationState, setPaginationState] = useState<PaginationModelNew>({
      current_page: 1,
      items_per_page: 20,
      total_items: 1,
      total_pages: 1
    })
    const [otherSurgeries, setOtherSurgeries] = useState([]) as any
    const [anchorSurgeries, setAnchorSurgeries] = useState(null)

    const toggleCalendar = (interval: 'start' | 'end') => {
      if (showCalendar === interval) {
        setShowCalendar('')
      } else {
        setShowCalendar(interval)
      }
    }

    const changeDate = (date: Date) => {
      if (showCalendar === 'start' && !moment(date).isSameOrBefore(dates.end)) {
        return setShowCalendar('')
      } else if (
        showCalendar === 'end' &&
        moment(dates[showCalendar]) &&
        !moment(date).isSameOrAfter(dates.start)
      ) {
        return setShowCalendar('')
      }
      if (showCalendar) {
        setDates({
          ...dates,
          [showCalendar]: date.toISOString(),
          label: 'personalized'
        })
      }
      return setShowCalendar('')
    }

    const changeInterval = (interval: string) => {
      const dates: IntervalProps =
        getInterval[interval as keyof typeof getInterval]
      dates.label = interval
      setDates(dates)
    }

    const getFilterValue = () => {
      if (dates.label === 'personalized') {
        return ''
      }
      return dates.label
    }

    const handlePreSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setPaginationState({ ...paginationState, current_page: 1 })
      setTitleStatus(selectedStatus)
    }

    const handleFiltersSubmit = async () => {
      setIsLoading(true)

      try {
        const payload = {
          hospital_id: currentHospitalId,
          start_date: dates.start!,
          end_date: dates.end!,
          schedule_status: selectedStatus as SchedulingStatus,
          search: searchField,
          page_number: paginationState.current_page,
          page_size: paginationState.items_per_page
        }

        const response =
          await surgicalOrderService.searchSurgicalOrdersSchedules(payload)

        return {
          data: response?.data.data?.map(
            (item: SurgicalSchedulingSectorModel) => ({
              ...item,
              info_badges: {
                patient_hospitalized: item.patient_hospitalized,
                robotic:
                  item.surgical_center === 'SALA ROBOTICA (C)' ? true : false,
                hemodinamica: normalizeText(item.surgical_center)
                  ?.toLowerCase()
                  .includes('hemodinamica')
                  ? true
                  : false,
                urgency: item.hospitalization_type === 'urgency' ? true : false,
                makoRobot:
                  item.technique === 'makoRobot' ||
                  item.technique === 'robô mako'
                    ? true
                    : false,
                expiredPassword:
                  item?.expired_password &&
                  item.expired_password ===
                    PasswordMaxDateValidationStatus.EXPIRED
                    ? true
                    : false,
                isErpIntegration: item.is_erp_integration,
                oncology_or_research: item.oncology_or_research,
                surgery_type: item.surgery_type,
                other_surgeries: item.other_surgeries
              },
              patient_name: capitalizeEachWordText(item.patient_name!),
              doctor_name: capitalizeEachWordText(item.doctor_name!),
              surgery_name: capitalizeEachWordText(item.surgery_name!),
              surgery_date: item?.surgery_date
                ? moment(item.surgery_date).utc().format('DD/MM/YYYY HH:mm[h]')
                : '',
              created_at_formatted: item?.created_at
                ? moment(item.created_at).utc().format('DD/MM/YYYY HH:mm[h]')
                : '',
              solicitation_date: item?.solicitation_date
                ? moment(item.solicitation_date)
                    .utc()
                    .format('DD/MM/YYYY HH:mm[h]')
                : '',
              requested_by: getShortName(item.requested_by_name!),
              scheduled_by: getShortName(item.scheduled_by_name!)
            })
          ),
          totalCount: response?.data?.page_info?.total_items
        }
      } catch (error) {
        toast.error('Não foi possível carregar os pedidos')
      } finally {
        setIsLoading(false)
      }
    }

    function getFirstAndLastName(fullName: string) {
      const names = fullName?.trim().split(' ')

      if (names?.length > 2) {
        return `${names[0]} ${names[names?.length - 1]}`
      }

      return fullName
    }

    const handleOpenOrderDetails = (surgical_order_id: number) => {
      history.push('/pedido', {
        id: surgical_order_id
      })
    }

    const OtherSurgeriesContainer = (otherSurgeries: any) => {
      return (
        <div style={{ height: '310px', overflow: 'hidden' }}>
          <p style={{ padding: '10px 10px 0px' }}>
            <SupportTextNew
              as="span"
              color="neutral900"
              size="xsmall"
              weight="bold"
              style={{ marginBottom: '10px' }}
            >
              {otherSurgeries?.otherSurgeries?.length} cirurgias em aberto
            </SupportTextNew>
          </p>

          <ScrollView width="100%" height="100%">
            {otherSurgeries?.otherSurgeries?.length > 0 ? (
              <S.CardScroll>
                {otherSurgeries?.otherSurgeries?.map(
                  (item: any, index: number) => (
                    <Fragment key={index}>
                      <S.CardContainer
                        onClick={() =>
                          handleOpenOrderDetails(item.surgical_order_id)
                        }
                        key={item.surgical_order_id}
                      >
                        <SupportTextNew
                          weight="semiBold"
                          size="xsmall"
                          className="order"
                          color="neutral900"
                        >
                          Nº aviso: {item.surgical_order_id}
                        </SupportTextNew>
                        <SupportTextNew
                          weight="bold"
                          size="xsmall"
                          className="doctor"
                          color="neutral900"
                        >
                          Médico (a): {getFirstAndLastName(item.doctor.name)}
                        </SupportTextNew>
                        <SupportTextNew
                          weight="bold"
                          size="xsmall"
                          className="unit"
                          color="neutral900"
                        >
                          Unidade: {item.hospital.name}
                        </SupportTextNew>
                      </S.CardContainer>
                      <DividerNew marginBottom="0" marginTop="0" />
                    </Fragment>
                  )
                )}
              </S.CardScroll>
            ) : (
              <p>Nenhum dado disponível</p>
            )}
          </ScrollView>
        </div>
      )
    }

    const InfoBadgesRender = (item: any) => {
      return (
        <S.BadgesWrapper>
          {item?.value?.patient_hospitalized && (
            <S.Badge color="white" bgColor="primary500">
              INT
            </S.Badge>
          )}
          {item?.value?.robotic && (
            <S.Badge bgColor="secondary400">ROB</S.Badge>
          )}
          {item?.value?.oncology_or_research && (
            <TooltipNew text="Oncológico" placement="bottom">
              <S.Badge bgColor="danger400">ONCO</S.Badge>
            </TooltipNew>
          )}
          {item?.value?.hemodinamica && (
            <S.Badge bgColor="terciary300">HEM</S.Badge>
          )}
          {item?.value?.makoRobot && (
            <S.Badge bgColor="secondary400">MAKO</S.Badge>
          )}
          {item?.value?.urgency && (
            <TooltipNew
              text={`Pedido com urgência ${
                item?.data?.scheduling_date
                  ? moment(
                      item?.data?.scheduling_date?.[0]?.expected_date
                    ).format('[-] DD/MM/YYYY')
                  : ''
              }`}
              type="alert"
              placement="top-start"
            >
              <AlertIcon className="urgency-icon" />
            </TooltipNew>
          )}
          {item?.value?.expiredPassword && (
            <TooltipNew
              text={
                <span>
                  Senha de autorização vencida
                  {/* <br />
                  Validade:{' '}
                  {moment(
                    item?.data?.finalize_surgical_order
                      .password_max_date_validation
                  )
                    .utc()
                    .format('DD/MM/YYYY')} */}
                </span>
              }
              type="standard"
              placement="top"
            >
              <S.TriangleAlertIconWrapper>
                <TriangleAlertIcon />
              </S.TriangleAlertIconWrapper>
            </TooltipNew>
          )}
          {item?.value?.isErpIntegration && (
            <TooltipNew text="Pedido Integrado" type="standard" placement="top">
              <S.IntegratedSurgicalOrderIconWrapper>
                <IntegratedSurgicalOrderIcon />
              </S.IntegratedSurgicalOrderIconWrapper>
            </TooltipNew>
          )}
          {item?.value?.surgery_type && (
            <TooltipNew
              text={
                SurgeryTypeLocated[item?.value?.surgery_type as SurgeryType]
              }
              placement="top"
            >
              <S.Badge bgColor="info400">
                {SurgeryType[item?.value?.surgery_type as SurgeryType]}
              </S.Badge>
            </TooltipNew>
          )}
          {item?.value?.other_surgeries?.length > 0 && (
            <>
              <S.Badge color="white" bgColor="transparent">
                <MenuSurgeriesRender item={item.value.other_surgeries} />
              </S.Badge>
            </>
          )}
        </S.BadgesWrapper>
      )
    }

    const ColumnHeaderDefault = (item: any) =>
      item?.column?.caption && (
        <S.ColumnHeaderDefault>{item.column.caption}</S.ColumnHeaderDefault>
      )

    const BoxDefaultRender = (item: any) =>
      item?.value && (
        <S.BoxDefault lineClamp>{capitalizeText(item.value)}</S.BoxDefault>
      )

    const SchedulingStatusRender = (item: any) =>
      item?.value && (
        <S.SchedulingStatus status={item.value} lineClamp>
          {SchedulingStatusLocated[item.value as SchedulingStatus]}
        </S.SchedulingStatus>
      )

    const SurgicalOrderIDRender = (item: any) =>
      item?.value &&
      item?.data?.active_status !== 'Pedido aberto' && (
        <S.BoxDefault centered>{item.value}</S.BoxDefault>
      )

    const CapitalizeTextRenderWithTooltip = (item: any) =>
      item?.value && (
        <TooltipNew text={item?.value} type="standard" placement="bottom">
          <S.BoxDefault lineClamp>{item.value}</S.BoxDefault>
        </TooltipNew>
      )

    const RequestedByRender = (item: any) => {
      return (
        <S.ProfileNameRounded>
          {item?.value && (
            <TooltipNew text={item.value} type="standard" placement="top-start">
              <span className="profile-name-rounded">
                {getNameInitials(item.value)}
              </span>
            </TooltipNew>
          )}
        </S.ProfileNameRounded>
      )
    }

    const ScheduledByRender = (item: any) => {
      return (
        <S.ProfileNameRounded>
          {item?.value && (
            <TooltipNew text={item.value} type="standard" placement="top-start">
              <span className="profile-name-rounded">
                {getNameInitials(item.value)}
              </span>
            </TooltipNew>
          )}
        </S.ProfileNameRounded>
      )
    }

    const handlePaginationChange = (currentPage: number) => {
      setPaginationState({ ...paginationState, current_page: currentPage + 1 })
    }

    const handlePageSizeChange = (pageSize: number) => {
      setPaginationState({
        ...paginationState,
        current_page: 1,
        items_per_page: pageSize
      })
      dataGridRef.current.instance.pageIndex(0)
    }

    const onRowClick = useCallback((data) => {
      const item = data.data as SurgicalSchedulingSectorModel

      if (item.scheduling_status === SchedulingStatus.SCHEDULED) {
        getSchedule(item.surgical_order_id)
        return
      }

      history.push('/setor-de-marcacao/agendar', item)
    }, [])

    const handleMenuSurgeries = (event: any, val: any) => {
      setOtherSurgeries(val.item)
      setAnchorSurgeries(event.currentTarget)
    }

    const handleCloseSurgeries = () => {
      setAnchorSurgeries(null)
    }

    const MenuSurgeriesRender = (surgery: any) => {
      return (
        <OtherSurgeriesIcon
          width={25}
          height={25}
          onClick={(e) => {
            e.stopPropagation()
            handleMenuSurgeries(e, surgery)
          }}
        />
      )
    }

    const getSchedule = async (surgical_order_id: number) => {
      try {
        setIsLoading(true)

        if (!surgical_order_id) return

        const response: any =
          await surgicalScheduleService.loadSurgicalSchedule({
            surgical_order_id: surgical_order_id
          })

        setSchedule(response?.data)
        setShowModalScheduleDetails(true)
      } catch (error) {
        toast.error('Ocorreu um erro ao buscar dados do agendamento')
      } finally {
        setIsLoading(false)
      }
    }

    const customStore = new CustomStore({
      // key: 'surgical_order_id',
      load: handleFiltersSubmit
    })

    const memoDataGrid = useMemo(() => {
      return (
        <>
          <DataGrid
            // keyExpr="surgical_order_id"
            ref={dataGridRef}
            dataSource={customStore}
            onRowClick={onRowClick}
          >
            <RemoteOperations paging={true} />
            <Column
              caption=""
              dataField="info_badges"
              alignment="center"
              allowFiltering={false}
              cellRender={InfoBadgesRender}
            />
            <Column
              caption="Status do agendamento"
              dataField="scheduling_status"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              cellRender={SchedulingStatusRender}
            />
            <Column
              caption="Nº aviso"
              dataField="surgical_order_id"
              alignment="center"
              allowFiltering={false}
              cellRender={SurgicalOrderIDRender}
              width="105px"
            />
            <Column
              caption="Data criação do aviso"
              dataField="created_at_formatted"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              cellRender={BoxDefaultRender}
              width="120px"
              visible={selectedStatus !== SchedulingStatus.CREATE_NEW_SCHEDULE}
            />
            <Column
              caption="Data da solicitação"
              dataField="solicitation_date"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              cellRender={BoxDefaultRender}
              width="120px"
              visible={selectedStatus !== SchedulingStatus.CREATE_NEW_SCHEDULE}
            />
            <Column
              caption="Data da cirurgia"
              dataField="surgery_date"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              cellRender={BoxDefaultRender}
              width="120px"
              visible={selectedStatus !== SchedulingStatus.CREATE_NEW_SCHEDULE}
            />
            <Column
              caption="Cirurgia"
              dataField="surgery_name"
              allowFiltering={false}
              cellRender={CapitalizeTextRenderWithTooltip}
            />

            <Column
              caption="Médico (a)"
              dataField="doctor_name"
              allowFiltering={false}
              cellRender={CapitalizeTextRenderWithTooltip}
            />

            <Column
              caption="Tempo médio"
              dataField="average_time"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              alignment="center"
            />

            <Column
              caption="Paciente"
              dataField="patient_name"
              allowFiltering={false}
              cellRender={CapitalizeTextRenderWithTooltip}
            />

            <Column
              caption="Convênio"
              dataField="health_insurance_name"
              allowFiltering={false}
              cellRender={BoxDefaultRender}
            />

            <Column
              caption="Solicit. por"
              dataField="requested_by_name"
              alignment="center"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              cellRender={RequestedByRender}
              visible={selectedStatus !== SchedulingStatus.CREATE_NEW_SCHEDULE}
            />

            <Column
              caption="Agend. por"
              dataField="scheduled_by_name"
              alignment="center"
              allowFiltering={false}
              headerCellRender={ColumnHeaderDefault}
              cellRender={ScheduledByRender}
              visible={selectedStatus !== SchedulingStatus.CREATE_NEW_SCHEDULE}
            />
            <Paging
              enabled={true}
              defaultPageSize={paginationState?.items_per_page}
              onPageIndexChange={handlePaginationChange}
              onPageSizeChange={handlePageSizeChange}
            />
            <Pager
              visible={true}
              showPageSizeSelector={true}
              allowedPageSizes={allowedPageSizes}
              showNavigationButtons={true}
              showInfo={true}
              infoText="Página {0} de {1} ({2} itens)"
            />
            <LoadPanel enabled={false} />
          </DataGrid>
        </>
      )
    }, [currentHospitalId, paginationState])

    return (
      <>
        <S.FiltersWrapper onSubmit={handlePreSubmit}>
          <TextFieldNew
            name="search_field"
            className="search_field"
            label="Buscar por pedido"
            labelOverlapping
            labelSize="xxsmall"
            labelWeight="semiBold"
            placeholder="Busque por paciente, procedimento ou n° do aviso"
            onInputChange={setSearchField}
            value={searchField}
          />
          <SelectFieldNew
            name="status"
            label="Status do agendamento"
            labelOverlapping
            labelSize="xxsmall"
            labelWeight="semiBold"
            initialValue="all"
            items={filterStatusValues}
            onInputChange={(val) => setSelectedStatus(val as SchedulingStatus)}
            value={selectedStatus}
          />
          <S.InputCalendar>
            <TextFieldNew
              name="date_start"
              label="Data início"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelOverlapping
              mask={dateMask}
              icon={<CalendarRange />}
              iconPosition="insideRight"
              iconIsButton
              iconColor="neutral700"
              iconBgColor="transparent"
              onIconClick={() => toggleCalendar('start')}
              value={dateInputFormat(moment(dates.start ?? '').toISOString())}
            />
            <S.StyledCalendar
              show={showCalendar === 'start'}
              onDateChange={(date: any) => changeDate(date as Date)}
              minDate={moment().subtract(1, 'year').endOf('day').toDate()}
              maxDate={moment().endOf('day').toDate()}
            />
          </S.InputCalendar>
          <S.InputCalendar>
            <TextFieldNew
              name="date_end"
              label="Data fim"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelOverlapping
              mask={dateMask}
              icon={<CalendarRange />}
              iconPosition="insideRight"
              iconIsButton
              iconColor="neutral700"
              iconBgColor="transparent"
              onIconClick={() => toggleCalendar('end')}
              value={dateInputFormat(
                moment(dates.end ?? '')
                  .subtract(4, 'hours')
                  .toISOString()
              )}
            />
            <S.StyledCalendar
              show={showCalendar === 'end'}
              onDateChange={(date: any) => changeDate(date as Date)}
              minDate={moment().subtract(1, 'year').endOf('day').toDate()}
              maxDate={moment().endOf('day').toDate()}
            />
          </S.InputCalendar>
          <SelectFieldNew
            label="Período"
            labelOverlapping
            labelSize="xxsmall"
            labelWeight="semiBold"
            initialValue="today"
            items={filterPeriodValues}
            onInputChange={changeInterval}
            placeholder={'Personalizado'}
            value={getFilterValue()}
          />
          <ButtonNew
            size="large"
            type="submit"
            // data-testid="new-surgical-order-button"
            style={{ fontSize: '14px' }}
          >
            Filtrar pedidos
          </ButtonNew>
        </S.FiltersWrapper>

        {memoDataGrid}

        <Menu
          style={{ marginLeft: '30px' }}
          anchorEl={anchorSurgeries}
          open={Boolean(anchorSurgeries)}
          onClose={handleCloseSurgeries}
        >
          <S.StyledSurgeryItem>
            <OtherSurgeriesContainer otherSurgeries={otherSurgeries} />
          </S.StyledSurgeryItem>
        </Menu>

        <ScheduleDetailsModal
          title="Cirurgia agendada"
          showModalDetails={showModalScheduleDetails}
          setShowModalDetails={setShowModalScheduleDetails}
          schedule={schedule}
        />
      </>
    )
  }
)

export default SchedulingSectorOrdersDataGrid

// ToDo: ADD new status here:
const filterStatusValues = [
  {
    label: 'Todos',
    value: SchedulingStatus.ALL
  },
  {
    label: SchedulingStatusLocated.CREATE_NEW_SCHEDULE,
    value: SchedulingStatus.CREATE_NEW_SCHEDULE
  },
  {
    label: SchedulingStatusLocated.SCHEDULED,
    value: SchedulingStatus.SCHEDULED
  },
  // {
  //   label: 'Solicitação de agendamento',
  //   value: SchedulingStatus.SCHEDULE_CHANGED_REQUEST // ??
  // },
  // {
  //   label: 'Solicitação de edição',
  //   value: 'edit_solicitation' // ??
  // },
  {
    label: SchedulingStatusLocated.SCHEDULING_CANCELED,
    value: SchedulingStatus.SCHEDULING_CANCELED
  },
  {
    label: SchedulingStatusLocated.SCHEDULE_CANCELLATION_REQUEST,
    value: SchedulingStatus.SCHEDULE_CANCELLATION_REQUEST
  }
]

const filterPeriodValues = [
  {
    label: 'Hoje',
    value: 'today'
  },
  {
    label: 'Ontem',
    value: 'yesterday'
  },
  {
    label: 'Semana',
    value: 'week'
  },
  {
    label: 'Mês',
    value: 'month'
  },
  {
    label: 'Trimestre',
    value: 'trimester'
  },
  {
    label: 'Semestre',
    value: 'semester'
  }
]

const getInterval = {
  today: {
    start: dateIntervals.START_OF_TODAY.toISOString(),
    end: dateIntervals.END_OF_TODAY.toISOString()
  },
  yesterday: {
    start: dateIntervals.ONE_DAY_AGO.toISOString(),
    end: dateIntervals.END_OF_TODAY.toISOString()
  },
  week: {
    start: dateIntervals.ONE_WEEK_AGO.toISOString(),
    end: dateIntervals.END_OF_TODAY.toISOString()
  },
  month: {
    start: dateIntervals.ONE_MONTH_AGO.toISOString(),
    end: dateIntervals.END_OF_TODAY.toISOString()
  },
  trimester: {
    start: dateIntervals.THREE_MONTHS_AGO.toISOString(),
    end: dateIntervals.END_OF_TODAY.toISOString()
  },
  semester: {
    start: dateIntervals.SIX_MONTHS_AGO.toISOString(),
    end: dateIntervals.END_OF_TODAY.toISOString()
  }
}
